import { Eye, EyeSlash } from '@phosphor-icons/react';
import { useState } from 'react';
import {
  FieldError,
  Input,
  Label,
  Text as AriaText,
  TextField as AriaTextField,
  ToggleButton,
  type TextFieldProps as AriaTextFieldProps,
} from 'react-aria-components';
import { cn } from '../lib/cn';
import { Icon } from './Icon';
import { uiText } from '../i18n';

export interface PasswordFieldProps extends Omit<AriaTextFieldProps, 'className' | 'type'> {
  label?: string;
  description?: string;
  errorMessage?: string;
  /** 'current-password' to sign in, 'new-password' to create one – password managers rely on it. */
  autoComplete: 'current-password' | 'new-password';
  className?: string;
}

/**
 * Password input with a "show" toggle: typing a password on a phone without seeing it is the
 * most common reason sign-ups fail. The toggle keeps one name and reports its state via
 * aria-pressed, so screen readers hear "Passwort anzeigen, gedrückt".
 */
export function PasswordField({
  label,
  description,
  errorMessage,
  className,
  ...props
}: PasswordFieldProps) {
  const [visible, setVisible] = useState(false);
  return (
    <AriaTextField
      {...props}
      type={visible ? 'text' : 'password'}
      className={cn('flex flex-col gap-gap-xs', className)}
    >
      <Label className="text-sm font-semibold text-text-secondary">
        {label ?? uiText().password}
      </Label>
      <div className="relative">
        <Input
          spellCheck={false}
          autoCapitalize="none"
          className={cn(
            'w-full min-h-touch-min rounded-control border border-border-default bg-surface-raised pl-inset-sm pr-touch-min text-md text-text-primary',
            'outline-none',
            'data-focused:border-border-focus data-focus-visible:outline-3 data-focus-visible:outline-border-focus',
            'data-invalid:border-feedback-danger',
          )}
        />
        <ToggleButton
          isSelected={visible}
          onChange={setVisible}
          aria-label={uiText().showPassword}
          className={cn(
            'absolute inset-y-0 right-0 grid min-w-touch-min place-items-center rounded-control text-text-secondary cursor-pointer',
            'outline-none data-focus-visible:outline-3 data-focus-visible:outline-border-focus',
            'data-hovered:text-text-primary',
          )}
        >
          <Icon icon={visible ? EyeSlash : Eye} size="md" />
        </ToggleButton>
      </div>
      {description && (
        <AriaText slot="description" className="text-sm text-text-muted">
          {description}
        </AriaText>
      )}
      <FieldError className="text-sm font-semibold text-feedback-danger">{errorMessage}</FieldError>
    </AriaTextField>
  );
}
